/**
 * Prompt Input Component
 * Textarea with preset scenarios and character/token count
 */

import { useState, useEffect } from 'react';
import { Zap, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SCENARIO_OPTIONS } from '@/lib/demo/preset-scenarios';

interface PromptInputProps {
  value: string;
  onChange: (value: string) => void;
  onAnalyze: () => void;
  isAnalyzing: boolean;
}

export function PromptInput({ value, onChange, onAnalyze, isAnalyzing }: PromptInputProps) {
  const [charCount, setCharCount] = useState(0);
  const [tokenCount, setTokenCount] = useState(0);

  useEffect(() => {
    setCharCount(value.length);
    // Simple token estimation: ~4 characters per token
    setTokenCount(Math.ceil(value.length / 4));
  }, [value]);

  const handlePresetChange = (scenarioId: string) => {
    const scenario = SCENARIO_OPTIONS.find((s) => s.id === scenarioId);
    if (scenario) {
      onChange(scenario.prompt);
    }
  };

  return (
    <div className="backdrop-blur-md bg-background/50 border border-border/50 rounded-xl p-6 shadow-xl">
      <h3 className="text-xl font-semibold mb-4">Prompt Input</h3>

      {/* Preset Scenarios */}
      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">Preset Scenarios</label>
        <Select onValueChange={handlePresetChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select a scenario..." />
          </SelectTrigger>
          <SelectContent>
            {SCENARIO_OPTIONS.map((scenario) => (
              <SelectItem key={scenario.id} value={scenario.id}>
                {scenario.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Textarea */}
      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">Enter your prompt...</label>
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="What is the capital of France?"
          rows={6}
          className="resize-none"
        />
        <div className="flex justify-between text-xs text-muted-foreground mt-2">
          <span>Characters: {charCount}</span>
          <span>Est. tokens: {tokenCount}</span>
        </div>
      </div>

      {/* Analyze Button */}
      <Button
        onClick={onAnalyze}
        disabled={!value.trim() || isAnalyzing}
        className="w-full mb-4 bg-accent hover:bg-accent/90 text-accent-foreground"
      >
        <Zap className="w-5 h-5 mr-2" />
        {isAnalyzing ? 'Analyzing...' : 'Analyze Routing'}
      </Button>

      {/* Privacy Notice */}
      <div className="flex items-start space-x-2 text-sm text-muted-foreground bg-black/20 backdrop-blur-sm p-3 rounded-lg">
        <Info className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
        <p>This demo runs in your browser. We never see your text.</p>
      </div>
    </div>
  );
}
